/**
 * Route table.
 *
 * The internal console is the prototype's own surface at the prototype's own addresses:
 * `#/admin/templates` in the frozen reference is `/admin/templates` here, and the screens behind
 * those addresses are the components the visual acceptance measures against that reference. §1 of
 * the brief makes the prototype the binding product definition, and this is what that means in the
 * router: there is no second internal design.
 *
 * Three areas, deliberately separated:
 *   /admin/…  the internal console (`V07InternalRoutes`), behind the real session. Everything it
 *             reads comes from the backend's projections and everything it writes goes through an
 *             authorized route.
 *   /c/:code  the canonical verification page (BACKEND §18), `/verify` beside it. Anonymous, no
 *             session restore, no bearer token.
 *   /claim/…  the recipient's one-time link (BACKEND §26). Anonymous. The raw token stays in the
 *             URL and in the request that uses it, and is never written to storage, to state that
 *             outlives the action, or to a log.
 *
 * All three areas render the PROTOTYPE's screens — `V07PublicSurface` and `V07InternalRoutes` —
 * so there is no second design anywhere in the application. The Phase-4 components that used to
 * answer the three public addresses no longer have a route.
 *
 * Public and claim routes are mounted outside the session guard so a recipient never meets a login
 * screen, and so an internal operator's session can never leak into a public page.
 *
 * `/` and `/en` are NOT here. Those two addresses are the marketing pages, which are separate Vite
 * entry points served as files by `server.mjs` (and by the dev middleware in `vite.config.ts`), so
 * this router never sees them. A route added for either address here would be dead in production
 * and would disagree with the server in development.
 */
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { V07InternalRoutes } from './v07/ProductSurface';
import { V07ClaimRoute, V07PublicCredentialRoute, V07VerifyRoute } from './v07/PublicSurface';

export function App(): ReactElement {
  return (
    <Routes>
      {/* anonymous surfaces: mounted before the guarded ones so no session is ever consulted */}
      <Route path="/c/:code" element={<V07PublicCredentialRoute />} />
      <Route path="/claim/:token" element={<V07ClaimRoute />} />
      <Route path="/verify" element={<V07VerifyRoute />} />

      <Route path="/admin/*" element={<V07InternalRoutes />} />

      {/*
       * The console's former addresses land on the prototype's. A bookmark that pointed at
       * `/templates` still arrives somewhere real rather than at a blank page.
       */}
      <Route path="/templates/*" element={<Navigate replace to="/admin/templates" />} />
      <Route path="/credentials/*" element={<Navigate replace to="/admin/credentials" />} />
      <Route path="/batches/*" element={<Navigate replace to="/admin/import" />} />
      <Route path="/assets" element={<Navigate replace to="/admin/assets" />} />
      <Route path="/settings/*" element={<Navigate replace to="/admin/settings/email" />} />
      <Route path="/login" element={<Navigate replace to="/admin/login" />} />
      <Route path="*" element={<Navigate replace to="/admin" />} />
    </Routes>
  );
}
